import type { SelectItem } from '@mantine/core';
import type { TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import type {
	FilterOption,
	FilterValue,
	FilterView,
} from '@repo/common/components/Filter/types';
import { useMemo, useState } from 'react';

import { trackEvent } from '@repo/common/analytics';
import { filterValueToLegacyFilter } from '@repo/common/components/Filter';
import type { FILTER_OPTIONS_DIVIDER } from '@repo/common/components/Filter/constants';
import type { DataTableSortStatus } from '@repo/mantine-datatable';
import {
	invalidateSearchViews,
	useAuthUser,
	useCreateSearchView,
	useUpdateSearchView,
} from '../../../api';
import { convertISearchViewToFilterView } from './utils';

interface UseFilterViewModalProps {
	onClose: (view?: FilterView | null) => void;
	view?: FilterView | null;
	selectedFilters: FilterValue[];
	selectedSort?: DataTableSortStatus;
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[];
}

export function useFilterViewModal({
	onClose,
	view,
	selectedFilters,
	selectedSort,
	filterOptions,
}: UseFilterViewModalProps) {
	const { user, workspace } = useAuthUser();
	const [name, setName] = useState<string>(view?.label || '');
	const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(
		view?.teams || []
	);

	const { data: teams } = useApiListTeamsV2({});
	const teamOptions = useMemo(
		() =>
			(teams ?? []).map(
				(team: TeamOut) =>
					({
						label: team.name,
						value: team.id,
						icon: team.icon,
					}) as SelectItem
			),
		[teams]
	);

	const handleTeamChange = (value: Array<string>) => {
		setSelectedTeamIds(value);
	};

	const handleNameChange = (value: string) => {
		setName(value);
	};

	const { mutateAsync: createSearchView } = useCreateSearchView({
		options: {
			onSuccess: () => {
				invalidateSearchViews(1);
				trackEvent('search/views/create', {}, user, workspace);
			},
		},
	});

	async function handleOnSave() {
		const createdView = await createSearchView({
			data: {
				name,
				teams: selectedTeamIds,
				selected_filters: await filterValueToLegacyFilter(
					filterOptions,
					selectedFilters
				),
				selected_sort_by: selectedSort?.columnAccessor ?? '',
				selected_sort_direction: selectedSort?.direction,
			},
		});
		onClose(convertISearchViewToFilterView(createdView, user));
	}

	const { mutateAsync: updateSearchView } = useUpdateSearchView({
		options: {
			onSuccess: () => {
				invalidateSearchViews(1);
				trackEvent('search/views/update', {}, user, workspace);
			},
		},
		disableOptimisticUpdate: true,
		disableInvalidation: true,
	});

	async function handleOnUpdate() {
		if (!view) {
			return;
		}

		const updatedView = await updateSearchView({
			data: {
				id: view.value,
				name,
				teams: selectedTeamIds,
				selected_filters: await filterValueToLegacyFilter(
					filterOptions,
					selectedFilters
				),
				selected_sort_by: selectedSort?.columnAccessor ?? '',
				selected_sort_direction: selectedSort?.direction,
			},
		});
		onClose(convertISearchViewToFilterView(updatedView, user));
	}

	return {
		teams: teamOptions,
		selectedTeamIds,
		name,
		handleTeamChange,
		handleNameChange,
		handleOnSave,
		handleOnUpdate,
	};
}
